import styled, { css } from 'styled-components';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import { commonInputStyles, FormInputProps } from 'brand-guidelines/input-styled-components';
import { TYPOGRAPHY } from 'brand-guidelines/typography';

import { DIRECTION } from './types';

export const textDefaults = css`
  ${TYPOGRAPHY.CIRCULAR.book}
  font-size: 15px;
  color: ${BRAND_COLORS.black};
  text-align: left;
`;

const directionMap = Object.freeze({
  [DIRECTION.UP]: 'bottom',
  [DIRECTION.DOWN]: 'top',
});

// convert up/down to bottom/top CSS properties (e.g. `bottom: 100%;`)
const getCssPositionProperty = (dropDirection: DIRECTION) =>
  directionMap[dropDirection] || directionMap[DIRECTION.DOWN];

interface AutocompleteWrapperProps {
  $disabled: boolean;
}
export const AutocompleteWrapper = styled.div<AutocompleteWrapperProps>`
  position: relative;

  outline: none !important;

  ${({ $disabled }) => !$disabled && css`
    cursor: pointer;
  `}
`;

const getOpenDropdownStyles = (dropDirection: DIRECTION, maxHeight: number) => css`
  min-height: 50px;
  max-height: ${maxHeight}px;
  ${getCssPositionProperty(dropDirection)}: 100%;
  outline-color: ${BRAND_COLORS.stroke};
  border-radius: 10px;
  opacity: 1;
`;

const getClosedDropdownStyles = (dropDirection: DIRECTION) => css`
  min-height: 0;
  max-height: 0;
  ${getCssPositionProperty(dropDirection)}: 80%;
  outline-color: ${BRAND_COLORS.white};
  border-radius: 0;
  opacity: 0;

  /* Fade-in effect */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 100px;
    background: linear-gradient(
      180deg,
      transparent,
      white 80%
    );
  }
`;

const handleOpenState = ({
  $shouldShow,
  $dropDirection,
  $maxHeight,
}: {
  $shouldShow: boolean;
  $dropDirection: DIRECTION;
  $maxHeight: number;
}) => $shouldShow
  ? getOpenDropdownStyles($dropDirection, $maxHeight)
  : getClosedDropdownStyles($dropDirection);

interface DropdownParentProps {
  $center: boolean;
  $shouldShow: boolean;
  $dropDirection: DIRECTION;
  $maxHeight: number;
}

export const DropdownParent = styled.div<DropdownParentProps>`
  position: absolute;
  z-index: 999;

  margin: 1px 0;

  container-type: inline-size;

  display: flex;
  flex-direction: column;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};

  background: ${BRAND_COLORS.white};
  outline-width: 1px;
  outline-style: solid;

  width: 100%;
  overflow: hidden;

  /* Transition animation for open/close */
  transition: all 0.15s ease-in-out, outline-color 0.05s ease-out;
  ${handleOpenState}
`;

export const NoResultsText = styled.p`
  @container (max-width: 130px) {
    padding: 0 12px;
  }

  ${textDefaults}
`;

export const InputWrapper = styled.div<FormInputProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  ${commonInputStyles}
  height: auto;

  &:focus-visible {
    outline: none;
  }
`;

interface SearchInputProps {
  $enableSearch: boolean;
}

const searchInputStyles = css`
  flex: 1;
  max-width: 100%;

  ${textDefaults}
  font-size: 16px;
  line-height: 24px;
  height: 24px;
`;

export const SearchInput = styled.input.attrs<SearchInputProps>(
  ({ $enableSearch }) => ({
    disabled: !$enableSearch,
  }),
)`
  ${searchInputStyles}

  background: 0;
  border: 0;
  padding: 0;

  /* antd global styles make this necessary */
  outline: none !important;
`;

export const ValueText = styled.p`
  ${searchInputStyles}

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;
